import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format, subDays, addDays } from 'date-fns';
import { ChevronLeft, ChevronRight, Heart } from 'lucide-react';
import api from '@/lib/api';
import type { CareLog } from '@/types';
import { CareLogPanel } from './CareLogPanel';

const SUMMARY_TYPES = [
  { type: 'urine', emoji: '💧', label: 'Urine', color: '#3b82f6' },
  { type: 'bm', emoji: '💩', label: 'BM', color: '#d97706' },
  { type: 'microseizure', emoji: '⚡', label: 'Seizures', color: '#ef4444' },
  { type: 'exercise', emoji: '🏃', label: 'Exercise', color: '#10b981' },
  { type: 'meal', emoji: '🍽️', label: 'Meals', color: '#f97316' },
  { type: 'medication', emoji: '💊', label: 'Meds', color: '#8b5cf6' },
] as const;

export function CareTab() {
  const [selectedDate, setSelectedDate] = useState(() => format(new Date(), 'yyyy-MM-dd'));

  const isToday = selectedDate === format(new Date(), 'yyyy-MM-dd');
  const dateDisplay = format(new Date(selectedDate + 'T12:00:00'), 'EEEE, MMM d');

  // Fetch summary for the selected date
  const { data: summary = {} } = useQuery({
    queryKey: ['care-logs-summary', selectedDate],
    queryFn: async () => {
      const res = await api.get(`/care-logs/summary?start=${selectedDate}&end=${selectedDate}`);
      return (res.data.summary || {}) as Record<string, number>;
    },
    staleTime: 1000 * 10,
    refetchInterval: 15000,
  });

  const handlePrevDay = () => setSelectedDate(format(subDays(new Date(selectedDate + 'T12:00:00'), 1), 'yyyy-MM-dd'));
  const handleNextDay = () => {
    const next = addDays(new Date(selectedDate + 'T12:00:00'), 1);
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    if (next <= today) {
      setSelectedDate(format(next, 'yyyy-MM-dd'));
    }
  };
  const handleToday = () => setSelectedDate(format(new Date(), 'yyyy-MM-dd'));

  const isFuture = new Date(selectedDate + 'T23:59:59') > new Date();

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-card rounded-xl border border-slate-700 p-4">
        <div className="flex items-center gap-2 mb-3">
          <Heart size={20} className="text-pink-400" />
          <h2 className="text-lg font-semibold text-white">Kai's Care Log</h2>
        </div>
        <p className="text-sm text-gray-400">
          Track health and care events throughout the day.
        </p>
      </div>

      {/* Date picker */}
      <div className="flex items-center justify-between bg-card rounded-xl border border-slate-700 p-3">
        <button
          onClick={handlePrevDay}
          className="p-2 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors"
        >
          <ChevronLeft size={18} />
        </button>
        <div className="text-center">
          <span className="text-white font-medium">{dateDisplay}</span>
          {isToday && (
            <span className="ml-2 text-[10px] bg-blue-500 text-white px-1.5 py-0.5 rounded">Today</span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {!isToday && (
            <button
              onClick={handleToday}
              className="px-2 py-1.5 text-xs bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 rounded-lg transition-colors"
            >
              Today
            </button>
          )}
          <button
            onClick={handleNextDay}
            disabled={isFuture}
            className="p-2 bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors disabled:opacity-30"
          >
            <ChevronRight size={18} />
          </button>
        </div>
      </div>

      {/* Quick log buttons + timeline */}
      <div className="bg-card rounded-xl border border-slate-700 p-4">
        <h3 className="text-sm font-medium text-gray-400 mb-3">Quick Log</h3>
        <CareLogPanel date={selectedDate} />
      </div>

      {/* Summary stats */}
      <div className="bg-card rounded-xl border border-slate-700 p-4">
        <h3 className="text-sm font-medium text-gray-400 mb-3">Daily Summary</h3>
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
          {SUMMARY_TYPES.map((st) => {
            const count = summary[st.type] || 0;
            return (
              <div
                key={st.type}
                className="flex flex-col items-center p-3 rounded-lg"
                style={{ backgroundColor: `${st.color}10`, border: `1px solid ${st.color}25` }}
              >
                <span className="text-xl mb-1">{st.emoji}</span>
                <span className="text-xl font-bold text-white">{count}</span>
                <span className="text-[10px] text-gray-400">{st.label}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
